import { ObjectLockModel } from "@plugins/common/types.ts";
import SecurityUtils from "@utils/security.utils";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import { loadLocaleMessages } from "@plugins/i18n.ts";

export function useLessonLockError() {
  const { t } = useI18n();
  const modal = useModal();

  const showLessonLockedModal = async (
    response: ObjectLockModel,
    currentLockResourceType: string,
    actionPrimaryFn: () => void,
    actionHideRequestFn: () => void,
    showErrorModalByLockableResourceType: boolean, // to be removed after the requirement for showing the new error lock modal is applied to all resource domains
    isEditFlow = true, // true => edit-blocked text (edit entry); false => action-blocked text (resume/move/delete + MOE actions)
    closeOnlyOnPrimary = false,
  ) => {
    await loadLocaleMessages("editor.course");

    let title: string;
    let content: string;

    if (response.missingBrowserTabUuid) {
      modal.removeAll();

      modal.showMessage("warning", {
        title: t("api_action_generic_error_title"),
        content: t("api_action_generic_error_message"),
        primaryBtnLabel: t("button_ok"),
        use_secondaryButton: false,
      });

      return;
    }

    if (response.lockedBySameUserDifferentTab === true) {
      title = t(
        isEditFlow
          ? "module_locked_in_another_tab_continue_edit_modal_title"
          : "module_locked_in_another_tab_modal_title",
      );

      content = t(
        isEditFlow
          ? "module_locked_in_another_tab_continue_edit_modal_content"
          : "module_locked_in_another_tab_modal_content",
      );

      modal.removeAll();

      modal.showMessage("warning", {
        title,
        content,
        primaryBtnLabel: t(
          isEditFlow ? "module_locked_in_another_tab_modal_edit_here_button" : "button_ok",
        ),
        use_secondaryButton: isEditFlow,
        ...(isEditFlow && {
          secondaryBtnLabel: t("module_locked_in_another_tab_modal_view_only_button"),
          actionSecondary: actionHideRequestFn,
        }),
        actionPrimary: isEditFlow ? actionPrimaryFn : actionHideRequestFn,
        actionHidden: () => {
          // empty function
        },
        actionHideRequest: actionHideRequestFn,
      });

      return;
    }

    if (showErrorModalByLockableResourceType) {
      const attemptedType = currentLockResourceType;
      const blockingType = response.resourceTypeLocked;
      const owners = response.resourceLockOwners ?? [];
      const owner = owners[0];
      const ownerName = SecurityUtils.sanitizeText(
        owner?.displayName ? `${owner.displayName} (${owner.name})` : (owner?.name ?? ""),
      );

      if (blockingType === LOCKABLE_RESOURCE_TYPE.LESSON) {
        title = t("module_locked_modal_title");
        content = isEditFlow
          ? t("module_locked_modal_content", { name: ownerName })
          : t("module_locked_action_blocked_content", { name: ownerName });
      } else if (
        attemptedType === LOCKABLE_RESOURCE_TYPE.LESSON &&
        blockingType === LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY
      ) {
        title = t("activity_level_edits_only_modal_title");
        content =
          owners.length > 1
            ? t("activity_level_edits_only_multiple_modal_content")
            : t("activity_level_edits_only_single_modal_content", { name: ownerName });
      } else {
        title = t("activity_locked_modal_title");
        content = t("activity_locked_modal_content", { name: ownerName });
      }
    } else {
      title = t("resource_locked_modal_title");
      content = t("resource_locked_modal_content", {
        userName: SecurityUtils.sanitizeText(response.user ?? ""),
      });
    }

    // Clear any open modal (e.g. a delete confirmation) before showing the lock modal.
    // ModalContainer.add() silently drops a MessageModal if one is already open with the same
    // component name, so need to remove it first to guarantee the lock modal renders.
    modal.removeAll();

    const closeOnlyOnModuleEditLock =
      showErrorModalByLockableResourceType &&
      currentLockResourceType === LOCKABLE_RESOURCE_TYPE.LESSON &&
      (response.resourceTypeLocked === LOCKABLE_RESOURCE_TYPE.LESSON ||
        response.resourceTypeLocked === LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY);

    modal.showMessage("warning", {
      title,
      content,
      primaryBtnLabel: t("button_ok"),
      use_secondaryButton: false,
      actionPrimary: () => {
        if (!closeOnlyOnPrimary) {
          actionHideRequestFn();
        }
      },
      actionHidden: () => {
        // empty function
      },
      actionHideRequest: () => {
        if (!closeOnlyOnModuleEditLock) {
          actionHideRequestFn();
        }
      },
    });
  };

  const showActivityLockedModal = async (
    response: ObjectLockModel,
    actionViewModeFn: () => void,
  ) => {
    await loadLocaleMessages("editor.course");

    const owner = response.resourceLockOwners?.[0];
    const ownerName = SecurityUtils.sanitizeText(
      owner?.displayName ? `${owner.displayName} (${owner.name})` : (owner?.name ?? ""),
    );

    modal.removeAll();

    modal.showMessage("warning", {
      title: t("activity_locked_modal_title"),
      content: t("activity_switch_locked_modal_content", {
        name: ownerName,
      }),
      primaryBtnLabel: t("button_go_to_view_mode"),
      secondaryBtnLabel: t("button_cancel"),
      use_secondaryButton: true,
      actionPrimary: actionViewModeFn,
    });
  };

  return {
    showLessonLockedModal,
    showActivityLockedModal,
  };
}
